<template>
  <form @submit="onSubmit">
    <MaintainProjectInfo
      v-model:project-settings="project.projectSettings"
      v-model:entities="entities"
      :property-types
      :property-types-ex
      :user-groups
      :checking-accounts
      :subdivisions
      :values
      :permissions
      :project-statuses
      :is-draw-zero="project.projectDrawInfo.drawNumber == 0"
      :set-field-value
      @user-group-updated="userGroupUpdated"
    ></MaintainProjectInfo>
    <SubmitOptions class="pt-5" @cancelled="$emit('cancel')"></SubmitOptions>
  </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { ProjectInfoWriteSchema, type Project } from "@/types/Projects";
import type { Entity } from "@/types/Entity";
import { useForm } from "vee-validate";
import { getAllEntities } from "@/services/EntitiesService";
import type { UserPermissions } from "@/types/User";
import type { Code } from "@/types/Codes";
import { entityAlreadyExistsInProject, getCodeTable, getProjectStatuses, getUserGroups } from "@/services/Utility";
import { CodeTableNumber } from "@/enums/CodeTable";
import { getCheckingAccounts } from "@/services/PaymentsService";
import type { CheckingAccount } from "@/types/CheckingAccount";
import { type Subdivision } from "@/types/Subdivision";
import { getSubdivisions } from "@/services/SubdivisionService";
import type { ProjectEntity } from "@/types/ProjectEntity";
import { ProjectRole } from "@/enums/ProjectRole";
import type { ProjectStatus } from "@/types/ProjectStatus";

const project = defineModel<Project>("project", { required: true });

interface Props {
  sourceOfFunds: ProjectEntity[];
  contacts: ProjectEntity[];
  permissions: UserPermissions;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  cancel: [];
  submit: [values: any];
}>();

const entities = ref<Entity[]>([]);
const propertyTypes = ref<Code[]>([]);
const propertyTypesEx = ref<Code[]>([]);
const projectStatuses = ref<ProjectStatus[]>([]);
const userGroups = ref<Code[]>([]);
const subdivisions = ref<Subdivision[]>([]);
const checkingAccounts = ref<CheckingAccount[]>([]);

let initialValues: any = project.value.projectInfo;

const { values, setFieldValue, handleSubmit } = useForm({
  validationSchema: toTypedSchema(ProjectInfoWriteSchema),
  initialValues: initialValues,
  keepValuesOnUnmount: true,
});

const userGroupUpdated = (): void => {
  setFieldValue("checkingAccount", null);
  setFieldValue("subdivision", null);
  checkingAccounts.value = [];
  subdivisions.value = [];
  updateCheckingAccounts();
  updateSubdivisions();
};

const updateCheckingAccounts = async (): Promise<void> => {
  getCheckingAccounts(values.userGroup, true).then((result: CheckingAccount[]) => {
    checkingAccounts.value = result;
  });
};

const updateSubdivisions = async (): Promise<void> => {
  getSubdivisions(values.userGroup).then((result: Subdivision[]) => {
    subdivisions.value = result;
  });
};

const onSubmit = handleSubmit((values: any): void => {
  if (entityAlreadyExistsInProject(values, props.sourceOfFunds, props.contacts, values.processor, ProjectRole.PROCESSOR)) return;
  if (entityAlreadyExistsInProject(values, props.sourceOfFunds, props.contacts, values.lender, ProjectRole.PRIMARY_LENDER)) return;
  if (entityAlreadyExistsInProject(values, props.sourceOfFunds, props.contacts, values.borrower, ProjectRole.PRIMARY_BORROWER)) return;
  if (entityAlreadyExistsInProject(values, props.sourceOfFunds, props.contacts, values.builder, ProjectRole.PRIMARY_BUILDER)) return;
  if (entityAlreadyExistsInProject(values, props.sourceOfFunds, props.contacts, values.inspector, ProjectRole.PRIMARY_INSPECTOR)) return;

  emit("submit", values);
});

onBeforeMount(async (): Promise<void> => {
  getUserGroups(true, true).then((result: Code[]) => {
    userGroups.value = result;
    if (userGroups.value.length == 1) setFieldValue("userGroup", userGroups.value[0]);
  });

  getAllEntities().then((result: Entity[]) => (entities.value = result));

  getCodeTable(CodeTableNumber.PROPERTY_TYPES).then((result: Code[]) => (propertyTypes.value = result));
  getCodeTable(CodeTableNumber.PROPERTY_TYPES_EX).then((result: Code[]) => (propertyTypesEx.value = result));
  updateCheckingAccounts();
  updateSubdivisions();
  getProjectStatuses().then((result: ProjectStatus[]) => {
    projectStatuses.value = result;
  });
});
</script>
